import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  Company,
  CompanyAsset,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyListResponse,
  CompanyAssetsResponse,
  MessageResponse,
} from '../../services/unified/types';

/**
 * Query hook for getting all companies
 */
export const useCompanies = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'companies'],
    queryFn: (): Promise<CompanyListResponse> => {
      return unifiedApiService.companies.getCompanies();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting a single company by ID
 */
export const useCompany = (id: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'companies', id],
    queryFn: (): Promise<Company> => {
      return unifiedApiService.companies.getCompanyById(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting company assets
 */
export const useCompanyAssets = (companyId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'companies', companyId, 'assets'],
    queryFn: (): Promise<CompanyAssetsResponse> => {
      return unifiedApiService.companies.getCompanyAssets(companyId);
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Mutation hook for creating a company
 */
export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateCompanyRequest): Promise<Company> => {
      return unifiedApiService.companies.createCompany(data);
    },
    onSuccess: () => {
      // Invalidate companies list to show new company
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'companies'] });
    },
  });
};

/**
 * Mutation hook for updating a company with optimistic updates
 */
export const useUpdateCompany = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyRequest }): Promise<Company> => {
      return unifiedApiService.companies.updateCompany(id, data);
    },
    // Optimistic update
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [...queryKeys.admin.all, 'companies', id] });
      
      // Snapshot the previous value
      const previousCompany = queryClient.getQueryData<Company>([...queryKeys.admin.all, 'companies', id]);
      
      // Optimistically update to the new value
      if (previousCompany) {
        queryClient.setQueryData<Company>([...queryKeys.admin.all, 'companies', id], {
          ...previousCompany,
          ...data,
        });
      }
      
      // Return context with the snapshot
      return { previousCompany };
    },
    onSuccess: (updatedCompany, variables) => {
      // Update the specific company in cache with server response
      queryClient.setQueryData([...queryKeys.admin.all, 'companies', variables.id], updatedCompany);
      
      // Invalidate companies list to reflect changes
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'companies'] });
    },
    // Rollback on error
    onError: (_err, { id }, context) => {
      if (context?.previousCompany) {
        queryClient.setQueryData([...queryKeys.admin.all, 'companies', id], context.previousCompany);
      }
    },
  });
};

/**
 * Mutation hook for deleting a company
 */
export const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<MessageResponse> => {
      return unifiedApiService.companies.deleteCompany(id);
    },
    onSuccess: (_, id) => {
      // Remove the company from cache
      queryClient.removeQueries({ queryKey: [...queryKeys.admin.all, 'companies', id] });
      
      // Invalidate companies list to reflect deletion
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'companies'] });
    },
  });
};

/**
 * Mutation hook for uploading company asset
 */
export const useUploadCompanyAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      companyId,
      file,
      assetType,
      isDefault = false,
    }: {
      companyId: string;
      file: File;
      assetType: 'logo' | 'image';
      isDefault?: boolean;
    }): Promise<CompanyAsset> => {
      return unifiedApiService.companies.uploadCompanyAsset(companyId, file, assetType, isDefault);
    },
    onSuccess: (_, variables) => {
      // Invalidate company assets to show new asset
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', variables.companyId, 'assets'] 
      });
      
      // If this is a default asset, invalidate the company details too
      if (variables.isDefault) {
        queryClient.invalidateQueries({ 
          queryKey: [...queryKeys.admin.all, 'companies', variables.companyId] 
        });
      }
    },
    onError: (error) => {
      console.error('Company asset upload failed:', error);
    },
  });
};

/**
 * Mutation hook for deleting company asset
 */
export const useDeleteCompanyAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ companyId, assetId }: { companyId: string; assetId: string }): Promise<MessageResponse> => {
      return unifiedApiService.companies.deleteCompanyAsset(companyId, assetId);
    },
    onSuccess: (_, variables) => {
      // Invalidate company assets to reflect deletion
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', variables.companyId, 'assets'] 
      });
      
      // Invalidate company details in case default asset was deleted
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', variables.companyId] 
      });
    },
    onError: (error) => {
      console.error('Company asset deletion failed:', error);
    },
  });
};

/**
 * Mutation hook for setting default company asset
 */
export const useSetDefaultCompanyAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ companyId, assetId }: { companyId: string; assetId: string }): Promise<CompanyAsset> => {
      return unifiedApiService.companies.setDefaultAsset(companyId, assetId);
    },
    onSuccess: (_, variables) => {
      // Invalidate company assets to reflect new default
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', variables.companyId, 'assets'] 
      });
      
      // Invalidate company details to update default_logo_url
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', variables.companyId] 
      });
    },
  });
};

/**
 * Mutation hook for saving an existing URL as a company asset
 */
export const useSaveUrlAsCompanyAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      companyId,
      url,
      assetType,
      isDefault = false,
    }: {
      companyId: string;
      url: string;
      assetType: 'logo' | 'image';
      isDefault?: boolean;
    }): Promise<CompanyAsset> => {
      return unifiedApiService.companies.saveUrlAsCompanyAsset(companyId, url, assetType, isDefault);
    },
    onSuccess: (_, variables) => {
      // Invalidate company assets to show new asset
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', variables.companyId, 'assets'] 
      });
      
      // If this is a default asset, invalidate the company details too
      if (variables.isDefault) {
        queryClient.invalidateQueries({ 
          queryKey: [...queryKeys.admin.all, 'companies', variables.companyId] 
        });
      }
    },
    onError: (error) => {
      console.error('Save URL as company asset failed:', error);
    },
  });
};
